import { z } from 'zod';
import { TOTAL_SEND_PAYLOAD_BUDGET } from './schemas/primitives.ts';
import {
  FetchMessageBodySchema,
  FetchRawMessageSchema,
  GetAttachmentSchema,
  GetMessageSchema,
  GetProfileNameSchema,
  GetProfilePhotoSchema,
  GetProfileSchema,
  GetThreadSchema,
  ListLabelsSchema,
  ListMessagesSchema,
  SearchMessagesSchema,
} from './schemas/read-actions.ts';
import {
  ArchiveMessageSchema,
  BatchModifyMessagesSchema,
  CreateLabelSchema,
  DeleteMessageSchema,
  ModifyMessageSchema,
  ResetUnsubscribeSchema,
  SnoozeMessageSchema,
  TrashMessageSchema,
  UnsubscribeSchema,
  UnsnoozeMessageSchema,
  UntrashMessageSchema,
} from './schemas/write-actions.ts';
import {
  DeleteDraftSchema,
  SaveDraftSchema,
  SendEmailSchema,
  UpdateDraftSchema,
} from './schemas/send-actions.ts';
import {
  GetCapabilitiesSchema,
  GetSignatureSchema,
  GetUnsubscribeInfoSchema,
  ListHistorySchema,
  RefreshSendAsSchema,
  RefreshSignatureSchema,
} from './schemas/meta-actions.ts';

export * from './schemas/primitives.ts';
export * from './schemas/read-actions.ts';
export * from './schemas/write-actions.ts';
export * from './schemas/send-actions.ts';
export * from './schemas/meta-actions.ts';

const CorrespondenceRequestDiscriminator = z.discriminatedUnion('action', [
  ListMessagesSchema,
  SearchMessagesSchema,
  GetMessageSchema,
  GetThreadSchema,
  GetAttachmentSchema,
  ListLabelsSchema,
  GetProfileSchema,
  GetProfilePhotoSchema,
  GetProfileNameSchema,
  ModifyMessageSchema,
  DeleteMessageSchema,
  TrashMessageSchema,
  UntrashMessageSchema,
  CreateLabelSchema,
  BatchModifyMessagesSchema,
  ArchiveMessageSchema,
  SendEmailSchema,
  SaveDraftSchema,
  UpdateDraftSchema,
  DeleteDraftSchema,
  ListHistorySchema,
  GetSignatureSchema,
  RefreshSignatureSchema,
  RefreshSendAsSchema,
  GetCapabilitiesSchema,
  FetchMessageBodySchema,
  FetchRawMessageSchema,
  GetUnsubscribeInfoSchema,
  UnsubscribeSchema,
  ResetUnsubscribeSchema,
  SnoozeMessageSchema,
  UnsnoozeMessageSchema,
]);

/**
 * Root request schema for the correspondence-api facade. The discriminated
 * union dispatches on `action`; cross-field invariants (e.g. "modify_message
 * requires at least one label delta") are enforced by the top-level
 * `superRefine` below. Per-action sub-schemas stay as pure `ZodObject` so they
 * remain eligible for `z.discriminatedUnion` (a `ZodEffects` branch breaks the
 * union).
 */
type RefineCtx = z.RefinementCtx;
type ParsedRequest = z.infer<typeof CorrespondenceRequestDiscriminator>;

/**
 * `organizationId` is optional in `BaseRequestSchema` because
 * `get_capabilities` is an adapter-global action that doesn't resolve an
 * account. For every other action we require it explicitly — omitting it
 * would otherwise fall through to `resolveUserOrganization().limit(1)`
 * and silently pick an arbitrary org for multi-org users.
 */
function refineOrganizationId(data: ParsedRequest, ctx: RefineCtx): void {
  if (data.action !== 'get_capabilities' && !data.organizationId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${data.action} requires organizationId`,
      path: ['organizationId'],
    });
  }
}

function refineLabelDelta(data: ParsedRequest, ctx: RefineCtx): void {
  if (data.action !== 'modify_message' && data.action !== 'batch_modify_messages') {
    return;
  }
  if (data.addLabels.length === 0 && data.removeLabels.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${data.action} requires addLabels or removeLabels to be non-empty`,
      path: ['addLabels'],
    });
  }
}

function refineDraftReference(data: ParsedRequest, ctx: RefineCtx): void {
  if (data.action !== 'save_draft') {
    return;
  }
  if (data.mode !== 'new' && !data.referenceMessageId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'reply/replyAll/forward require referenceMessageId',
      path: ['referenceMessageId'],
    });
  }
}

function refineSendBudget(data: ParsedRequest, ctx: RefineCtx): void {
  if (data.action !== 'send_email') {
    return;
  }
  const bodyHtmlBytes = data.bodyHtml.length;
  const bodyTextBytes = data.bodyText?.length ?? 0;
  const attachmentBytes = data.attachments.reduce((sum, a) => sum + a.size, 0);
  const total = bodyHtmlBytes + bodyTextBytes + attachmentBytes;
  if (total > TOTAL_SEND_PAYLOAD_BUDGET) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `send_email payload (${total} bytes) exceeds budget of ${TOTAL_SEND_PAYLOAD_BUDGET} bytes`,
      path: ['attachments'],
    });
  }
}

/**
 * `fromName` without `fromAlias` is invalid — the From header has no email
 * to render alongside the display name. The adapter would silently drop
 * `fromName` rather than emit a malformed header; rejecting at the contract
 * layer makes the failure mode explicit.
 */
function refineFromNameRequiresAlias(data: ParsedRequest, ctx: RefineCtx): void {
  if (data.action !== 'send_email') {
    return;
  }
  if (data.fromName && !data.fromAlias) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'fromName requires fromAlias',
      path: ['fromName'],
    });
  }
}

/**
 * An on-behalf attempt's stored replay envelope describes the wire payload
 * only — it cannot address attachment bytes, which live outside the jsonb
 * document. An attempt carrying attachments would therefore be replayed
 * incomplete, so the contract fails closed rather than dispatching a send
 * that cannot be reconstructed.
 */
function refineOnBehalfAttachments(data: ParsedRequest, ctx: RefineCtx): void {
  if (data.action !== 'send_email' || !data.onBehalf) {
    return;
  }
  if (data.attachments.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'send_email on behalf of a shared account does not support attachments',
      path: ['attachments'],
    });
  }
}

export const CorrespondenceRequestSchema = CorrespondenceRequestDiscriminator.superRefine(
  (data, ctx) => {
    refineOrganizationId(data, ctx);
    refineLabelDelta(data, ctx);
    refineDraftReference(data, ctx);
    refineSendBudget(data, ctx);
    refineFromNameRequiresAlias(data, ctx);
    refineOnBehalfAttachments(data, ctx);
  },
);

export type CorrespondenceRequest = z.infer<typeof CorrespondenceRequestSchema>;
export type CorrespondenceAction = CorrespondenceRequest['action'];

/**
 * Derived from the discriminator union — adding a schema branch above
 * automatically adds the action here. Drift is structurally impossible
 * (compare to a hand-maintained list, which the rule file calls out as a
 * silent failure surface).
 */
export const ALL_ACTIONS: readonly CorrespondenceAction[] =
  CorrespondenceRequestDiscriminator.options.map(
    (option) => option.shape.action.value,
  ) as readonly CorrespondenceAction[];

/**
 * Action-category tuples. Each `satisfies readonly CorrespondenceAction[]`
 * gives an exact-tuple literal whose element type is the matching union of
 * action names — `typeof TUPLE[number]` resolves to the narrowed category
 * subtype (e.g. `WriteAction`). Sets derive from the tuples so runtime and
 * type-level membership can never drift.
 *
 * Why tuples + sets instead of plain sets: Phase 4 frontend hooks need the
 * narrowed union types (`ReadAction`, `WriteAction`) to type-constrain the
 * action argument of `useCorrespondenceQuery` / `useCorrespondenceMutation`.
 * A plain `Set<CorrespondenceAction>` loses the per-member literal typing.
 */
const WRITE_ACTION_NAMES = [
  'modify_message',
  'delete_message',
  'trash_message',
  'untrash_message',
  'create_label',
  'batch_modify_messages',
  'archive_message',
  'send_email',
  'save_draft',
  'update_draft',
  'delete_draft',
  'refresh_signature',
  'refresh_send_as',
  // Newsletter-unsubscribe executor (ADR 0087). A write — it mutates
  // external state (the sender's list) and records an audit row — but NOT
  // in `APPROVER_ONLY_SHARED_ACTIONS`: any active member with account
  // access may trigger it; it never flows through the send approval
  // workflow.
  'unsubscribe',
  // Withdraws an active unsubscribe request (status superseded) - mutates the
  // same audit table, so it is a write like `unsubscribe`.
  'reset_unsubscribe',
  // Snooze writes the visibility row and may move mail at the provider. Any
  // member with write access to the account may snooze; not approver-only.
  'snooze_message',
  'unsnooze_message',
] as const satisfies readonly CorrespondenceAction[];

export type WriteAction = (typeof WRITE_ACTION_NAMES)[number];

export const WRITE_ACTIONS: ReadonlySet<CorrespondenceAction> = new Set<CorrespondenceAction>(
  WRITE_ACTION_NAMES,
);

const READ_ACTION_NAMES = [
  'list_messages',
  'search_messages',
  'get_message',
  'get_thread',
  'get_attachment',
  'list_labels',
  'get_profile',
  'get_profile_photo',
  'get_profile_name',
  'list_history',
  'get_signature',
  'get_capabilities',
  // `fetch_message_body` writes to `email_bodies` as a side effect, but the
  // semantic from the caller's POV is "load the body the polling window
  // missed" — it never mutates provider state, never sends, never destroys.
  // Classified read so the capability map advertises it as universally
  // supported instead of locked behind the destructive-action guard.
  'fetch_message_body',
  // Pure read — fetches the original message source from the provider and
  // returns it inline, persisting nothing.
  'fetch_raw_message',
  // Like `fetch_message_body`, may persist captured header state as a side
  // effect, but never mutates provider state — semantically "tell me whether
  // this email can be unsubscribed from".
  'get_unsubscribe_info',
] as const satisfies readonly CorrespondenceAction[];

export type ReadAction = (typeof READ_ACTION_NAMES)[number];

export const READ_ACTIONS: ReadonlySet<CorrespondenceAction> = new Set<CorrespondenceAction>(
  READ_ACTION_NAMES,
);

/**
 * Actions that destroy or mutate user-visible state. Used by the frontend
 * `useProviderCapability` hook to choose the conservative default during
 * the capability-map loading/error window: destructive actions render
 * `{supported: false, reason: 'CAPABILITY_MAP_LOADING'}` rather than
 * optimistically unblocking a Send button that would 422.
 *
 * NOTE: Non-destructive writes (`untrash_message`, `create_label`,
 * `refresh_signature`, `refresh_send_as`) are deliberately absent —
 * they're recoverable and surface a conservative "supported" default.
 */
const DESTRUCTIVE_ACTION_NAMES = [
  'send_email',
  'save_draft',
  'update_draft',
  'delete_draft',
  'delete_message',
  'trash_message',
  'archive_message',
  'batch_modify_messages',
  'modify_message',
] as const satisfies readonly CorrespondenceAction[];

export type DestructiveAction = (typeof DESTRUCTIVE_ACTION_NAMES)[number];

export const DESTRUCTIVE_ACTIONS: ReadonlySet<CorrespondenceAction> = new Set<CorrespondenceAction>(
  DESTRUCTIVE_ACTION_NAMES,
);

export const FAN_OUT_ELIGIBLE: ReadonlySet<CorrespondenceAction> = new Set<CorrespondenceAction>([
  'list_messages',
  'search_messages',
  'list_labels',
]);

// Type-predicate form so callers narrow `action` to the write subtype after
// the check, without having to re-cast inside downstream branches.
export function isWriteAction(action: CorrespondenceAction): action is WriteAction {
  return WRITE_ACTIONS.has(action);
}

export function isReadAction(action: CorrespondenceAction): action is ReadAction {
  return READ_ACTIONS.has(action);
}

export function isDestructiveAction(action: CorrespondenceAction): action is DestructiveAction {
  return DESTRUCTIVE_ACTIONS.has(action);
}

export function isFanOutEligible(action: CorrespondenceAction): boolean {
  return FAN_OUT_ELIGIBLE.has(action);
}

// Re-export wire types so `@/lib/correspondence/contracts` (which does
// `export * from './contracts.ts'`) transparently surfaces Request/Response
// lookup maps, data shapes, input aliases, and capability-envelope types
// to frontend hooks.
export type * from './action-types.ts';

// Runtime helpers (type guards) that frontend hooks need at runtime, not
// just at type-check. Exported separately because `export type *` above
// strips value exports.
export { isFanOutEnvelope } from './action-types.ts';
